<template>
  <div class="acct-settings">
    <el-drawer :visible.sync="drawerVisible" :with-header="false" size="460px" :append-to-body="true">
      <div class="acct-panel">
        <!-- 顶栏 -->
        <div class="acct-panel__top">
          <div class="acct-panel__title">
            <span class="acct-panel__title-main">{{ $t("accountSettings.title") }}</span>
            <span class="acct-panel__title-sub">{{ $t("accountSettings.subtitle") }}</span>
          </div>
          <button type="button" class="acct-panel__close" @click="drawerVisible = false">
            <i class="el-icon-close"></i>
          </button>
        </div>

        <div class="acct-panel__body">
          <!-- 加载失败提示 -->
          <div v-if="loadError" class="acct-error">
            <i class="el-icon-warning-outline acct-error__icon"></i>
            <p class="acct-error__text">{{ $t("accountSettings.loadError") }}</p>
            <el-button size="small" @click="loadAccount">{{ $t("accountSettings.retry") }}</el-button>
          </div>

          <section v-else class="acct-section" v-loading="loading">
            <!-- 登录邮箱（只读） -->
            <div class="acct-field">
              <label class="acct-field__label">{{ $t("accountSettings.loginEmail") }}</label>
              <el-input :value="form.userName" disabled></el-input>
              <p class="acct-field__hint">{{ $t("accountSettings.loginHint") }}</p>
            </div>

            <!-- 手机号 -->
            <div class="acct-field">
              <label class="acct-field__label">{{ $t("accountSettings.phone") }}</label>
              <el-input v-model="form.phone" :placeholder="$t('accountSettings.pPhone')"></el-input>
            </div>

            <!-- 时区 -->
            <div class="acct-field">
              <label class="acct-field__label">{{ $t("accountSettings.timeZone") }}</label>
              <el-select
                v-model="form.timeZone"
                class="acct-field__select"
                clearable
                :no-data-text="$t('common.noData')"
                :no-match-text="$t('common.noData')"
                :placeholder="$t('accountSettings.pTimeZone')"
              >
                <el-option v-for="item in timeZoneList" :key="item.id" :label="item.label" :value="item.value"></el-option>
              </el-select>
              <p class="acct-field__hint">{{ $t("accountSettings.timeZoneHint") }}</p>
            </div>

            <!-- 国家（user/update 必填字段） -->
            <div class="acct-field">
              <label class="acct-field__label">{{ $t("addUser.country") }}</label>
              <el-select
                v-model="form.country"
                class="acct-field__select"
                :no-data-text="$t('common.noData')"
                :no-match-text="$t('common.noData')"
                :placeholder="$t('addUser.pCountry')"
              >
                <el-option v-for="item in countryList" :key="item.id" :label="item.label" :value="item.value"></el-option>
              </el-select>
            </div>

            <div class="acct-section__actions">
              <el-button size="small" @click="drawerVisible = false">{{ $t("common.cancel") }}</el-button>
              <el-button size="small" type="primary" :loading="saving" @click="save">
                {{ $t("accountSettings.save") }}
              </el-button>
            </div>
          </section>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
  import { mapTimeZoneList } from "@/utils/timezoneLabels";

  export default {
    name: "AccountSettings",
    props: {
      visible: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        loading: false,
        saving: false,
        loadError: false,
        record: {},
        timeZoneList: [],
        countryList: [],
        form: {
          userName: "",
          phone: "",
          timeZone: "",
          country: ""
        }
      };
    },
    computed: {
      drawerVisible: {
        get() {
          return this.visible;
        },
        set(val) {
          this.$emit("update:visible", val);
        }
      }
    },
    watch: {
      visible(val) {
        if (val) {
          this.loadAccount();
          this.loadTimeZones();
        }
      }
    },
    methods: {
      // 拉取当前登录用户的账户信息。
      // 顺序：登录快照（即时展示）→ 后台静默用 user/list 刷新 → 无快照时 user/list →
      // 管理员再试 getUserById（管理端接口，普通账号调用会 4xx 触发全局错误提示）。
      loadAccount() {
        this.loading = true;
        this.loadError = false;
        if (this.trySnapshot()) {
          this.loading = false;
          // 静默刷新：修复快照过期（他端/管理员改过资料）与快照缺字段（country 等）的问题
          this.loadByUsername(true);
          return;
        }
        this.loadByUsername(false);
      },
      // 快照命中返回 true（store LOGIN 写入 localStorage）
      trySnapshot() {
        try {
          const snap = JSON.parse(localStorage.getItem("vtm-profile"));
          if (snap && String(snap.id) === String(this.$cookies.get("uid") || "")) {
            this.applyRecord(snap);
            return true;
          }
        } catch (e) {
          /* ignore */
        }
        return false;
      },
      // user/list?userName=<登录名>，按 uid 匹配自己的记录。
      // silent=true 时为快照后的后台刷新：失败不进入错误态，成功则更新记录与快照。
      loadByUsername(silent) {
        let that = this;
        let userName = that.$cookies.get("userName") || "";
        that.$api
          .request(that.$url.User.listUserByUsername, that.$method.get, { userName })
          .then(res => {
            const raw = res.code === 0 ? res.data : null;
            const list = Array.isArray(raw) ? raw : [];
            const uid = String(that.$cookies.get("uid") || "");
            // 只认自己的 id：用户名是模糊匹配，唯一结果也可能是别人（例如子账号）
            const rec = list.find(u => String(u.id) === uid) || null;
            if (rec) {
              that.loading = false;
              that.applyRecord(rec);
              that.syncSnapshot(rec);
            } else if (!silent) {
              that.loadByIdForAdmin();
            }
          })
          .catch(() => {
            if (!silent) {
              that.loadByIdForAdmin();
            }
          });
      },
      // 将服务器记录同步进登录快照（保持快照新鲜、补齐缺失字段）
      syncSnapshot(rec) {
        try {
          const snap = JSON.parse(localStorage.getItem("vtm-profile")) || {};
          localStorage.setItem("vtm-profile", JSON.stringify({ ...snap, ...rec, id: rec.id || snap.id }));
        } catch (e) {
          /* ignore */
        }
      },
      // 回退 2：仅管理员账号尝试 getUserById（其他账号无权限，避免触发全局错误提示）
      loadByIdForAdmin() {
        let that = this;
        const uid = String(that.$cookies.get("uid") || "");
        const roleId = String(that.$cookies.get("roleId") || "");
        if (roleId !== "0" && uid !== "1") {
          that.loading = false;
          that.loadError = true;
          return;
        }
        that.$api
          .request(that.$url.User.getUserById, that.$method.get, { uId: uid })
          .then(res => {
            that.loading = false;
            if (res.code === 0 && res.data) {
              that.applyRecord(res.data);
            } else {
              that.loadError = true;
            }
          })
          .catch(() => {
            that.loading = false;
            that.loadError = true;
          });
      },
      applyRecord(data) {
        this.record = data;
        this.form.userName = data.userName || "";
        this.form.phone = data.phone || "";
        this.form.timeZone = data.timeZone || "";
        this.form.country = data.country || "";
      },
      // 拉取时区/国家字典（时区用友好名称）
      loadTimeZones() {
        let that = this;
        that.$api.dict("gp_time_zone").then(res => {
          if (res.code === 0) {
            that.timeZoneList = mapTimeZoneList(res.data);
          }
        });
        that.$api.dict("gp_country_list").then(res => {
          if (res.code === 0) {
            that.countryList = res.data;
          }
        });
      },
      // 保存手机号 / 时区 / 国家（user/update 要求 phone、country、timeZone 等必填）
      save() {
        let that = this;
        if (!that.form.phone || !that.form.timeZone || !that.form.country) {
          that.$message.warning(that.$t("accountSettings.requiredWarn"));
          return;
        }
        that.saving = true;
        // 只发送本页可改的字段；其余（邮箱、姓名、角色等）由后端按本人已存记录补齐。
        // 不回传整条记录：快照可能缺字段或已过期，回传会覆盖管理员改过的资料。
        let obj = {
          id: String(that.$cookies.get("uid") || that.record.id || ""),
          phone: that.form.phone,
          timeZone: that.form.timeZone,
          country: that.form.country
        };
        that.$api
          .request(that.$url.User.update, that.$method.put, obj)
          .then(res => {
            that.saving = false;
            if (res.code === 0) {
              that.$notify({
                title: that.$t("common.tip"),
                message: that.$t("accountSettings.saveSuccess"),
                type: "success"
              });
              // 时区变更后同步登录态使用的 zone cookie（与登录时写入方式一致）
              that.$cookies.set("zone", that.form.timeZone, { expires: 7 });
              // 同步资料快照，保证下次打开显示最新值
              try {
                const snap = JSON.parse(localStorage.getItem("vtm-profile")) || {};
                localStorage.setItem("vtm-profile", JSON.stringify({ ...snap, ...obj, id: snap.id || obj.id }));
              } catch (e) {
                /* ignore */
              }
              that.drawerVisible = false;
            } else {
              that.$message.warning(res.msg);
            }
          })
          .catch(() => {
            that.saving = false;
          });
      }
    }
  };
</script>

<style scoped lang="scss">
  .acct-panel {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: var(--vtm-bg);
  }
  .acct-panel__top {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 16px 18px;
    background: var(--vtm-navy);
    color: #fff;
    flex-shrink: 0;
  }
  .acct-panel__title {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
  .acct-panel__title-main {
    font-family: var(--vtm-display);
    font-size: 18px;
    font-weight: 600;
  }
  .acct-panel__title-sub {
    font-size: 12px;
    color: #b9c6d3;
  }
  .acct-panel__close {
    border: none;
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
    width: 28px;
    height: 28px;
    border-radius: 7px;
    cursor: pointer;
    flex-shrink: 0;

    &:hover {
      background: rgba(255, 255, 255, 0.22);
    }
  }
  .acct-panel__body {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
  }

  .acct-section {
    padding: 14px;
    border: 1px solid var(--vtm-line);
    border-radius: var(--vtm-r-md);
    background: var(--vtm-surface);
  }
  .acct-section__actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid var(--vtm-line);
  }

  .acct-field {
    margin-bottom: 16px;

    &:last-of-type {
      margin-bottom: 0;
    }
  }
  .acct-field__label {
    display: block;
    margin-bottom: 6px;
    font-size: 12.5px;
    font-weight: 600;
    color: var(--vtm-heading);
  }
  .acct-field__select {
    width: 100%;
  }
  .acct-field__hint {
    margin: 6px 0 0;
    font-size: 12px;
    color: var(--vtm-ink-3);
  }

  .acct-error {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 40px 16px;
    text-align: center;
  }
  .acct-error__icon {
    font-size: 28px;
    color: var(--vtm-warn);
  }
  .acct-error__text {
    margin: 0;
    font-size: 13px;
    color: var(--vtm-ink-2);
  }
</style>
